import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { redirectOrPageReload } from '../../utils/handleRedirects';
import { SigningModeType } from '../../shared/constants/signingModeType';
import { SIGNING_FLOW_TYPE_MAP } from '../../shared/constants/signingFlowType';
import { getSigningFlowType } from '../../utils/getSigningFlowType';
import {
    MIXPANEL_SIGNING_MODE_TYPE_MAP,
    MixpanelSigningFlowType,
    MixpanelSigningModeType,
} from '../../events/mixpanel/mixpanel.constants';
import { globalMixpanelReporter } from '../../events/mixpanel/mixpanel.reporter';
import { globalBigQueryReporter } from '../../events/bigQuery/bigQuery.reporter';
import { setSocialSignupProviderCookie } from '../../utils/setSocialSignupProviderCookie';
import { reportIdentificationCompletion } from '../../utils/reportIdentificationCompletion';
import { ContextProps, IdentificationContextProviderProps } from './IdentificationContext.types';

export const IdentificationContext = React.createContext<ContextProps | undefined>(undefined);

export const IdentificationContextProvider = ({
    children,
    initialMode,
    initialEmail,
    initialUsername,
    isModal,
    isModalOpen,
    isSticky,
    isCodeActivationSkippable,
    isSocialFlow: initialIsSocialFlow = false,
    skipActivation: initialSkipActivation = false,
    closeModal,
    skipQuestionnaire,
    variant = 'default',
    redirectUrl = '',
    source,
    blockedActionType,
    afterSigningSuccess,
    inLynxesIdentificationResetPasswordRollout = false,
    inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout = false,
}: IdentificationContextProviderProps) => {
    const [mode, _setMode] = useState<SigningModeType>(initialMode);
    const modeRef = useRef(mode);
    const [isSocialFlow, _setIsSocialFlow] = useState(initialIsSocialFlow);
    const isSocialFlowRef = useRef(isSocialFlow);
    const [skipActivation, setSkipActivation] = useState(initialSkipActivation);
    const [email, setEmail] = useState<string>(initialEmail || '');
    const [username, setUserName] = useState<string>(initialUsername || '');
    const [password, setPassword] = useState<string>();
    const [shouldNavigateOnClose, setShouldNavigateOnClose] = useState(false);
    const redirectUrlRef = useRef<string>(redirectUrl);

    const setRedirectUrl = useCallback((url: string) => {
        redirectUrlRef.current = url;
        globalBigQueryReporter.enrichContext({ redirectUrl: url });
        globalMixpanelReporter.enrichContext({ redirectUrl: url });
    }, []);

    const navigateToRedirectUrl = useCallback(async () => {
        await redirectOrPageReload(redirectUrlRef.current);
    }, []);

    const finishSigningFlowAndNavigate = useCallback(async () => {
        if (modeRef.current === 'sign_up' && isSocialFlowRef.current) {
            setSocialSignupProviderCookie();
        }

        reportIdentificationCompletion();

        if (afterSigningSuccess) {
            afterSigningSuccess({
                redirectUrl: redirectUrlRef.current,
                signingFlowType: getSigningFlowType({
                    mode: modeRef.current,
                    isSocialFlow: isSocialFlowRef.current,
                }),
            });
            return;
        }

        await navigateToRedirectUrl();
    }, [afterSigningSuccess, navigateToRedirectUrl]);

    const handleSetIsSocialFlow = useCallback<React.Dispatch<React.SetStateAction<boolean>>>((valueOrCallback) => {
        if (typeof valueOrCallback === 'function') {
            _setIsSocialFlow((prev) => {
                const newValue = valueOrCallback(prev);
                isSocialFlowRef.current = newValue;
                return newValue;
            });
            return;
        }

        isSocialFlowRef.current = valueOrCallback;
        _setIsSocialFlow(valueOrCallback);
    }, []);

    const handleSetMode = useCallback((mode: SigningModeType) => {
        modeRef.current = mode;
        _setMode(mode);

        const bqEnrichment = {
            mode,
            flow: mode === 'sign_up' ? SIGNING_FLOW_TYPE_MAP.signUp : SIGNING_FLOW_TYPE_MAP.signIn,
        };
        globalBigQueryReporter.enrichContext(bqEnrichment);

        const mixpanelEnrichment = {
            mode: MIXPANEL_SIGNING_MODE_TYPE_MAP[mode],
            flow: mode === 'sign_up' ? 'Sign Up' : 'Sign In',
        } satisfies { mode: MixpanelSigningModeType; flow: MixpanelSigningFlowType };
        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
    }, []);

    useEffect(() => {
        if (isModal && !isModalOpen && shouldNavigateOnClose) {
            finishSigningFlowAndNavigate();
        }
    }, [isModal, isModalOpen, shouldNavigateOnClose, finishSigningFlowAndNavigate]);

    useEffect(() => {
        globalBigQueryReporter.enrichContext({ redirectUrl });
        globalMixpanelReporter.enrichContext({ redirectUrl });
    }, [redirectUrl]);

    const contextValue = useMemo(
        () => ({
            mode,
            email,
            username,
            variant,
            isModal,
            closeModal,
            setEmail,
            setUserName,
            setMode: handleSetMode,
            finishSigningFlowAndNavigate,
            isSticky,
            isSocialFlow,
            setIsSocialFlow: handleSetIsSocialFlow,
            skipQuestionnaire,
            blockedActionType,
            isCodeActivationSkippable,
            password,
            setPassword,
            setRedirectUrl,
            skipActivation,
            setSkipActivation,
            source,
            navigateToRedirectUrl,
            enableShouldNavigateOnClose: () => setShouldNavigateOnClose(true),
            inLynxesIdentificationResetPasswordRollout,
            inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout,
        }),
        [
            mode,
            email,
            username,
            variant,
            isModal,
            isSocialFlow,
            closeModal,
            handleSetMode,
            finishSigningFlowAndNavigate,
            isSticky,
            skipQuestionnaire,
            blockedActionType,
            isCodeActivationSkippable,
            password,
            setRedirectUrl,
            skipActivation,
            source,
            navigateToRedirectUrl,
            inLynxesIdentificationResetPasswordRollout,
            handleSetIsSocialFlow,
            inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout,
        ]
    );

    return <IdentificationContext.Provider value={contextValue}>{children}</IdentificationContext.Provider>;
};

export const useIdentificationContext = () => {
    const value = useContext(IdentificationContext);

    if (value === undefined) {
        throw new Error('useIdentificationContext must be used within a IdentificationContext.Provider');
    }

    return value;
};
